import {
    UseMutationResult,
    useMutation,
    useQueryClient,
    useQuery,
    UseQueryResult,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import {
    LoadOrderTasksFiltersRequest,
    LoadOrderTasksFiltersResponse,
    OrderBaselineTasksRequest,
    OrderBaselineTasksResponse,
    loadOrderTasksFiltersRequestSchema,
    loadOrderTasksFiltersResponseSchema,
    orderBaselineTasksRequestSchema,
    orderBaselineTasksResponseSchema,
} from './orderTasks.types';

const LOAD_FILTERS_QUERY_KEY = 'OrderTasksLoadFilters';
const ORDER_TASKS_MUTATION_KEY = 'OrderBaselineTasks';

// ---- Load Order Tasks Filters ----

const loadOrderTasksFilters = async (request: LoadOrderTasksFiltersRequest): Promise<LoadOrderTasksFiltersResponse> => {
    try {
        // Validate request
        loadOrderTasksFiltersRequestSchema.parse(request);
        
        const response = await createPacerApi().post(`${API_ROOT}baselinetask/order-tasks-filters`, { 
            json: request 
        }).json();
        
        return loadOrderTasksFiltersResponseSchema.parse(response);
    } catch (error) {
        console.error('loadOrderTasksFilters API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to load order tasks filters: ${error.message}`);
        }
        throw new Error('Failed to load order tasks filters: Unknown error');
    }
};

export const useLoadOrderTasksFilters = (
    request: LoadOrderTasksFiltersRequest,
    options?: { enabled?: boolean }
): UseQueryResult<LoadOrderTasksFiltersResponse> => {
    return useQuery<LoadOrderTasksFiltersResponse>({
        queryKey: [LOAD_FILTERS_QUERY_KEY, request],
        queryFn: () => loadOrderTasksFilters(request),
        enabled: options?.enabled ?? true,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// ---- Order Baseline Tasks ----

const orderBaselineTasks = async (request: OrderBaselineTasksRequest): Promise<OrderBaselineTasksResponse> => {
    try {
        // Validate request
        orderBaselineTasksRequestSchema.parse(request);
        
        const response = await createPacerApi().post(`${API_ROOT}baselinetask/order-tasks`, { 
            json: request 
        }).json();
        return orderBaselineTasksResponseSchema.parse(response);
    } catch (error) {
        console.error('orderBaselineTasks API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to order baseline tasks: ${error.message}`);
        }
        throw new Error('Failed to order baseline tasks: Unknown error');
    }
};

export const useOrderBaselineTasks = (
    callback?: (results: OrderBaselineTasksResponse) => void
): UseMutationResult<OrderBaselineTasksResponse, Error, OrderBaselineTasksRequest> => {
    const queryClient = useQueryClient();
    
    return useMutation<OrderBaselineTasksResponse, Error, OrderBaselineTasksRequest>({
        mutationKey: [ORDER_TASKS_MUTATION_KEY],
        mutationFn: orderBaselineTasks,
        onSuccess: (data) => {
            // Invalidate related queries
            queryClient.invalidateQueries({ queryKey: [LOAD_FILTERS_QUERY_KEY] });
            
            if (callback) {
                callback(data);
            }
        },
        onError: (error) => {
            console.error('Order baseline tasks mutation error:', error);
        },
    });
};